import "./cf7/cf7";

class AtnlSelectClass {
	constructor(openAnimationMs = 200) {

		// Config
		this.selectClass = '.atnlsel';
		this.openAnimationMs = openAnimationMs;
		this.activeSelect = false;

		// Debouncers
		this.debounceChengeEvent;
		this.debounceHandleState = {};
		this.debounceScroll;
		this.debounceScrollInterval;

		// Key buffer for letters.
		this.keyBuffer = '';
		this.keyBufferTimeout = null;
		this.lastKeyMatchedIndex = -1;

		this.init();
	}

	init() {
		this.setEventsListeners();
		this.setNavigationKeys();
	}

	// BEHAVIOUR FUNCTIONS
	open(el, focusToggle = false) {
		const { select, toggle, list, listWindow } = this.getElements(el);

		if (!select || select?.classlist?.contains('no-toggle') ) return;

		list.removeAttribute('inert')
		setTimeout(() => {
			toggle.setAttribute('aria-expanded', "true")
			list.setAttribute('aria-hidden', "false")
		}, this.isRadio(select) && 100 )

		// Close Open Select.
		this.setActiveSelect(select);

		// Set scroll listener
		this.setScrollListener(listWindow);

		focusToggle && this.giveFocus(toggle,true);
	}

	close(el, focusToggle = false) {
		const { select, toggle, list, listWindow} = this.getElements(el);
		if (!select || select?.classlist?.contains('no-toggle') ) return;

		// Remove scroll listener
		this.setScrollListener(listWindow,false);

		this.setActiveSelect(select, true);

		setTimeout(() => {
			list.setAttribute('aria-hidden', "true");
			list.setAttribute('inert', '');
			toggle.setAttribute('aria-expanded', "false");
		}, this.isRadio(select) && 100 ); // Prevents error of "blocks aria-hidden"

		focusToggle && this.giveFocus(toggle);
	}

	optionChecker(target, forceState = null, triggerEvent = true) {
		const { toggle, labels } = this.getElements(target);
		const option = this.getOption(target);
		if (!option) return;

		let input = option.querySelector('input')

		input.checked = forceState ?? !input.checked;

		option.setAttribute('aria-selected', input.checked)

		if (this.isRadio(input)){
			toggle.getAttribute('aria-expanded') === 'true' && this.close(input, true );
			toggle.querySelector('span').innerText = input.checked ? option.textContent.trim() : labels.toggle;
			input.checked && this.clearAriaSelect(option);
		}
		this.handleStates(input);

		triggerEvent && this.triggerChangeEvent(input)

		return true
	}

	clear(el, focusToggle = false) {
		const { options, toggle } = this.getElements(el);
		if (!options?.length) return;
		options.forEach(opt => this.optionChecker(opt, false));
		this.clearAriaSelect();
		focusToggle && this.giveFocus(toggle)
	}

	selectAll(el, forceState = null) {
		const { options, select } = this.getElements(el);
		if (!options?.length || !select.dataset.sel) return;
		let state = forceState ?? parseInt(select.dataset.sel) !== options.length
		options.forEach(opt => this.optionChecker(opt, state));
		this.clearAriaSelect();
	}

	makeHoverFocusElement(el){
		const {select} = this.getElements(el);
		if( !window.matchMedia("(pointer:fine)").matches || !select || select.querySelector('[aria-expanded=false]') || !el.closest('[tabindex="0"]:not([role="group"])') ) return;
		el.closest('[tabindex="0"]').focus()
	}

	goToNextEl(e) {
		let focusTree = this.getFocusTree(document.activeElement);
		if (!Array.isArray(focusTree) || focusTree.length === 0) return;
		const currentIndex = focusTree.indexOf(document.activeElement);
		if (currentIndex >= 0 && currentIndex < focusTree.length - 1) {
			focusTree[currentIndex + 1].focus();
		}
	}

	goToPrevEl(e) {
		const focusTree = this.getFocusTree(document.activeElement);
		if (!Array.isArray(focusTree) || focusTree.length === 0) return;
		const currentIndex = focusTree.indexOf(document.activeElement);
		if (currentIndex > 0) {
			focusTree[currentIndex - 1].focus();
		}
	}

	scrollList(direction = 'up') {
		let listWindow = this.activeSelect && this.activeSelect.listWindow;
		if (!listWindow) return;
		this.debounceScrollInterval && clearInterval(this.debounceScrollInterval);
		this.debounceScroll && clearTimeout(this.debounceScroll);
		this.debounceScroll = setTimeout(() => {
			this.debounceScrollInterval = setInterval(() => {
				direction === 'up' ? listWindow.scrollTop -= 3 : listWindow.scrollTop += 3
			}, 20);
		}, 250);
	}

	// STATE FUNCTIONS
	setActiveSelect(select, remove = false) {
		if (remove) {
			this.activeSelect = this.activeSelect.select === select ? false : this.activeSelect;
			return true;
		}
		this.activeSelect && this.activeSelect.select !== select && this.close(this.activeSelect)
		this.activeSelect = this.getElements(select);
		return true;
	}

	clearAriaSelect(exception = false) {
		const { options } = this.getElements(exception);
		if (!options?.length) return;
		options.forEach(el => el !== exception && el.setAttribute('aria-selected', 'false'))
	}

	handleStates(el) {
		const { select, toggle, options, clearBt, selectAllBt, labels } = this.getElements(el);
		if (!select) return

		const selectedOptions = Array.from(options).filter(el => el.getAttribute('aria-selected') === 'true');
		const nSelected = selectedOptions.length
		const { aria_clear, aria_select_all, aria_selected_singular, aria_selected_plural, aria_selected_more, main } = labels;


		const handleCount = () => {
			toggle.querySelector('span').dataset.sel = nSelected;
			select.dataset.sel = nSelected;

			if (nSelected === 1 || nSelected === options.length) {
				select.dataset.state = 'true'
			}
			if (nSelected > 1 && nSelected < options.length) {
				select.dataset.state = 'mixed'
			}
			if (nSelected === 0) {
				select.dataset.state = 'false'
			}
		}

		const handleBtSelectAllLabel = () => {
			selectAllBt && selectAllBt.setAttribute('aria-label', nSelected === options.length ? aria_clear : aria_select_all);
		}

		const handleBtClear = () => {
			if (!clearBt) return;
			!nSelected ? clearBt.setAttribute('inert', '') : clearBt.removeAttribute('inert');
		}

		const handleToogle = () => {
			let selectedOptionsString = selectedOptions.slice(0, 2)
				.map(opt => opt.textContent.trim())
				.join(',')

			let finalAria = '';
			finalAria = 1 === nSelected ? aria_selected_singular + ',' + selectedOptionsString : '';
			finalAria = 2 <= nSelected ? aria_selected_plural.replace(',', ',' + nSelected + ' ') + ':' + selectedOptionsString : finalAria;
			finalAria += 2 < nSelected ? ' ' + aria_selected_more.replace('%s', nSelected - 2) : '';

			toggle.setAttribute('aria-label', finalAria || main)
		}

		this.debounceHandleState[ select.dataset.id ] && clearTimeout(this.debounceHandleState[ select.dataset.id ]);
		this.debounceHandleState[ select.dataset.id ] = setTimeout(() => {
			handleCount();
			handleBtSelectAllLabel()
			handleBtClear();
			handleToogle();
		}, 50)
	}

	handleBtsScrollState(listWindow) {
		const scrollTop = listWindow.scrollTop;
		const scrollHeight = listWindow.scrollHeight;
		const clientHeight = listWindow.clientHeight;
		const btScrollUp = listWindow.parentElement.querySelector(`${this.selectClass}__bt-scroll--up`);
		const btScrollDown = listWindow.parentElement.querySelector(`${this.selectClass}__bt-scroll--down`);

		btScrollUp && scrollTop > 0
			? btScrollUp.classList.add('show')
			: btScrollUp.classList.remove('show')
		btScrollDown && Math.ceil(scrollTop + clientHeight) < Math.ceil(scrollHeight)
			? btScrollDown.classList.add('show')
			: btScrollDown.classList.remove('show')
	}

	// LISTENERS & EVENTS
	triggerChangeEvent(input) {
		if (!input) return;

		clearTimeout(this.debounceChengeEvent);

		this.debounceChengeEvent = setTimeout(() => {

			const event = new CustomEvent('change', {
				bubbles: true,
				detail: {
					origin: this.selectClass,
					els: this.getElements(input),
				},
			});
			input.dispatchEvent(event);

		}, 100);
	}

	setEventsListeners() {
		// Toggle - Open & Close.
		window.addEventListener('click', (e) => {
			const el = e.target.closest('button') || (e.target.classList.value.includes(`__head`) ? e.target : false);
			const { select, toggle } = this.getElements(el);

			const activeSelect = this.activeSelect.select;

			if (toggle && ( toggle === el || e.target.classList.value.includes(`__head`) ) && !toggle.closest(`${this.selectClass}.no-toggle`)) {
				toggle.blur()
				toggle.getAttribute('aria-expanded') === "true"
					? this.close(toggle, true)
					: this.open(toggle, true);
			}

			// Close inactive selects
			document.querySelectorAll(`${this.selectClass}:not(.no-toggle)`)?.forEach(element => {
				if (element !== select && (!e.target?.closest(this.selectClass) || ! (e.target?.closest(this.selectClass) === activeSelect )) ) {
					this.close(element);
				}
			})
		}, false);

		// Option checker
		window.addEventListener('click', (e) => {
			if (e.target.classList.contains('atnlsel__option')) {
				e.preventDefault();
				this.optionChecker(e.target)
				!this.isRadio(e.target) && this.giveFocus(e.target, true);
			}
		}, false);

		// When assistive tecnologies are on, it can check the input without passing through optionchecker, we solve this here.
		window.addEventListener('change', (e) => {
			const isCustom = e.detail?.origin === this.selectClass

			if( isCustom || !e.target.closest(this.selectClass)) return;

			e.target.checked = !e.target.checked;
			e.target.closest('label').click()
		}, false);

		// Clear and select all Buttons
		window.addEventListener('click', (e) => {
			const clear = e.target.closest('button.atnlsel__bt-clear');
			const selAll = e.target.closest('button.atnlsel__bt-all');
			if (clear) {
				clear.blur();
				this.clear(clear, true)
			}
			selAll && this.selectAll(selAll);
		}, false);

		// Change hover for focus
		window.addEventListener('mouseover', (e) => {
			this.makeHoverFocusElement(e.toElement || e.relatedTarget || e.target)
		})

		// Scroll Buttons
		window.addEventListener('mouseover', (e) => {
			const target = e.target;
			if( target.closest(`${this.selectClass}__bt-scroll.show`) ){
				target.classList.value.includes('--up')
					? this.scrollList('up')
					: this.scrollList('down')
			}else{
				clearInterval(this.debounceScrollInterval);
				clearTimeout(this.debounceScroll);
			}
		})
	}

	setNavigationKeys(){

		window.addEventListener('keydown', (e) => {

			const option = this.getOption(e.target );

			if( ! this.activeSelect ) return

			switch (e.key) {
				case ' ':
				case 'Space':
				case 'Enter':
					if( option ){
						e.preventDefault();
						option.click()
					}
					break;
				case 'Escape':
					e.preventDefault();
					e.stopPropagation();
					e.target.blur();
					this.close(select, true)
					break;
				case 'ArrowDown':
					e.preventDefault()
					this.goToNextEl(e);
					break;
				case 'ArrowUp':
					e.preventDefault()
					this.goToPrevEl(e);
					break;
				default:
					// Check if its a key letter to search options.
					let keyLetter = this.activeSelect?.list;
					keyLetter = keyLetter && !e.ctrlKey && !e.altKey && !e.metaKey
					keyLetter = keyLetter && e.key.length === 1 && /^[a-zA-Z0-9]$/.test(e.key)
					keyLetter && this.keyMapNavigate(e.key);
			}
		}, true)

	}

	keyMapNavigate(letter) {
		const options = Array.from(this.activeSelect?.options || []);
		if (!options?.length) return;

		clearTimeout(this.keyBufferTimeout);
		this.keyBuffer += letter.toLowerCase();

		this.keyBufferTimeout = setTimeout(() => {
			this.keyBuffer = '';
			this.lastKeyMatchedIndex = -1;
		}, 300);

		let matchIndex = -1;
		const search = this.keyBuffer;
		const currentIndex = this.lastKeyMatchedIndex === -1 ? options.findIndex(option => option === document.activeElement) : this.lastKeyMatchedIndex;

		for (let i = 1; i <= options.length; i++) {
			let idx = (currentIndex + i) % options.length;
			const text = options[idx].textContent.trim().toLowerCase();
			if (text.startsWith(search)) {
				matchIndex = idx;
				break;
			}
		}

		if (matchIndex !== -1 ) {
			this.lastKeyMatchedIndex = matchIndex;
			options[matchIndex].focus();
		}
	}

	setScrollListener(listWindow, add = true){
		if( add ){
			setTimeout(() => {
				this.handleBtsScrollState(listWindow)
				this.scrollListener = this.throttle(() => this.handleBtsScrollState(listWindow), 200);
				listWindow.addEventListener('scroll', this.scrollListener, false);
			}, this.openAnimationMs + 50);
		}else{
			listWindow.removeEventListener('scroll', this.scrollListener, false);
		}
	}

	// HELPER FUNCTION
	getElements(child) {
		const select = child && child.closest && child.closest(this.selectClass);

		if( select?.atnlsel )
			return select.atnlsel

		let labelsHolder = select && select.querySelector('div');
		let labels = labelsHolder && select.querySelector('div').textContent.trim().replace(/[“”]/g, '"')
		try{
			labels = labels ? JSON.parse(labels) : {};
		}catch(e){
			console.error('Failed to parse labels JSON in Atnl Select:', select, error);
		}
		labelsHolder && labelsHolder.remove();

		const els = {
			select: select,
			toggle: select && select.querySelector('[role="combobox"]'),
			list: select && select.querySelector('[role="listbox"]'),
			listWindow: select && select.querySelector(`${this.selectClass}__window`),
			options: select && select.querySelectorAll('[role="option"]'),
			clearBt: select && select.querySelector(`${this.selectClass}__bt-clear`),
			selectAllBt: select && select.querySelector(`${this.selectClass}__bt-all`),
			labels:  labels,
		}

		if( select )
			select.atnlsel = els

		return els;
	}

	getOption(el){
		const { select } = this.getElements(el)
		return select && el.closest('[role="option"]')
	}

	getFocusTree(el) {
		const { select } = this.getElements(el);
		return select ? Array.from(select.querySelectorAll('[tabindex]:not([tabindex="-1"]):not([inert]):not([role="group"])')) : false
	}

	giveFocus(el, blurBefore = false, ms = 100){
		blurBefore && el.blur();
		setTimeout(() => {
			el.focus()
		}, ms);
	}

	isRadio(el) {
		return el?.closest(`${this.selectClass}.is-radio`)
	}

	throttle(func, limit) {
		let lastFunc;
		let lastRan;
		return function () {
			const context = this;
			const args = arguments;
			if (!lastRan) {
				func.apply(context, args)
				lastRan = Date.now();
			} else {
				clearTimeout(lastFunc);
				lastFunc = setTimeout(() => {
					if ((Date.now() - lastRan) >= limit) {
						func.apply(context, args);
						lastRan = Date.now();
					}
				}, limit - (Date.now() - lastRan));
			}
		}
	}
}

const AtnlSelect = new AtnlSelectClass();
export default AtnlSelect;
